import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Buildings,
  House,
  Lightning,
  Drop,
  Flame,
  Package,
  LinkSimple,
  X,
} from '@phosphor-icons/react';

export interface TableField {
  fieldName: string;
  displayName: string;
}

export interface MapperAsset {
  id: string;
  name: string;
  parentAssetId: string | null;
  unitId: string | null;
  buildingId: string;
  assetTypeId: number;
  typeName: string;
  fieldValues: Record<string, any>;
  assetCategoryId: number;
  categoryName: string;
}

export interface TreeNode {
  asset: MapperAsset;
  children: TreeNode[];
  depth: number;
}

const TYPE_ICONS: Record<string, any> = {
  'Electriciteit': Lightning,
  'Elektriciteit': Lightning,
  'Gas': Flame,
  'Water': Drop,
};

// Left-border accent color per type — clean white cards with colored indicator
const TYPE_ACCENT: Record<string, string> = {
  'Electriciteit': 'border-l-amber-500',
  'Elektriciteit': 'border-l-amber-500',
  'Gas':           'border-l-rose-500',
  'Water':         'border-l-sky-500',
};

const DEFAULT_ACCENT = 'border-l-slate-400';

interface MapperNodeCardProps {
  node: TreeNode;
  tableFields: TableField[];
  onSetParent: (assetId: string) => void;
  onRemoveParent: (assetId: string) => void;
  depth?: number;
}

const MapperNodeCard: React.FC<MapperNodeCardProps> = ({
  node,
  tableFields,
  onSetParent,
  onRemoveParent,
  depth = 0,
}) => {
  const { asset, children } = node;
  const TypeIcon = TYPE_ICONS[asset.typeName] || Package;
  const accent = TYPE_ACCENT[asset.typeName] || DEFAULT_ACCENT;
  const isChild = node.depth > 0;
  const colorClass = `border-l-4 ${accent} ${isChild ? 'bg-muted/30 border-border/60' : 'bg-background border-border'}`;

  return (
    <div key={asset.id} className="relative">
      {depth > 0 && (
        <div className="absolute -top-3 left-6 w-px h-3 bg-border" />
      )}

      <div className={`block rounded-lg border p-3 shadow-sm transition-shadow hover:shadow-md ${colorClass}`}>
        <div className="flex items-start gap-2">
          <TypeIcon className="h-5 w-5 mt-0.5 flex-shrink-0 text-foreground/70" weight="duotone" />
          <div className="flex-1 min-w-0">
            {/* Header row: name + type badge */}
            <div className="flex items-center gap-2 mb-0.5">
              <a href={`/asset/${asset.id}`} className="text-sm font-semibold truncate hover:underline">
                {asset.name}
              </a>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">{asset.typeName}</Badge>
            </div>

            {/* Location */}
            {asset.unitId ? (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <House className="h-3 w-3" />
                <span>{asset.unitId}</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Buildings className="h-3 w-3" />
                <span>Gebouw-niveau</span>
              </div>
            )}

            {/* Table fields */}
            {tableFields.length > 0 && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                {tableFields.map(f => {
                  const val = asset.fieldValues?.[f.fieldName];
                  if (val == null || val === '') return null;
                  return (
                    <span key={f.fieldName} className="text-[11px] text-muted-foreground">
                      <span className="font-medium text-foreground/70">{f.displayName}:</span> {String(val)}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Parent link action */}
            <div className="flex items-center gap-1 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                onClick={(e) => { e.preventDefault(); onSetParent(asset.id); }}
              >
                <LinkSimple className="h-3 w-3 mr-1" />
                {asset.parentAssetId ? 'Wijzig ouder' : 'Koppel aan ouder'}
              </Button>
              {asset.parentAssetId && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 text-[11px] text-destructive hover:text-destructive"
                  onClick={(e) => { e.preventDefault(); onRemoveParent(asset.id); }}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Children */}
      {children.length > 0 && (
        <div className="ml-6 mt-1 space-y-1 relative">
          <div className="absolute left-0 top-0 bottom-0 w-px bg-border" />
          {children.map((child) => (
            <div key={child.asset.id} className="relative pl-6">
              <div className="absolute left-0 top-5 w-6 h-px bg-border" />
              <div className="absolute left-0 top-[17px] w-2 h-2 rounded-full bg-border" />
              <MapperNodeCard
                node={child}
                tableFields={tableFields}
                onSetParent={onSetParent}
                onRemoveParent={onRemoveParent}
                depth={depth + 1}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapperNodeCard;
